/**
 * Profile → delete the account: every business the person owns goes with it (listed, with the
 * § 132 BAO warning and a way to export each first), their access to other businesses ends, and
 * they are signed out. Needs the account's email typed.
 */
import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { deleteAccount, deleteRefusal } from "@/lib/api/account";
import { useAuth } from "@/lib/session/auth-context";
import { useWorkspace } from "@/lib/session/workspace-context";

export function DeleteAccountCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { workspaces, workspace: active, select } = useWorkspace();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user) return null;

  const owned = workspaces.filter((workspace) => workspace.owner === user.id);
  const matches = typed.trim().toLowerCase() === user.email.trim().toLowerCase();

  async function remove() {
    setBusy(true);
    try {
      await deleteAccount(typed);
      toast.success(t("deleteAccount.done"));
      // Leave the protected pages before the session ends (see AGENTS.md, logging out).
      await navigate("/");
      logout();
    } catch (error) {
      const reason = deleteRefusal(error);
      toast.error(
        reason === "subscription"
          ? t("deleteAccount.subscription")
          : reason === "mismatch"
            ? t("deleteAccount.mismatch")
            : t("deleteAccount.failed"),
      );
      setBusy(false);
    }
  }

  return (
    <Card className="border-destructive/40" data-testid="delete-account">
      <CardHeader>
        <CardTitle>{t("deleteAccount.title")}</CardTitle>
        <CardDescription>{t("deleteAccount.hint")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {owned.length > 0 && (
          <Alert>
            <TriangleAlert className="size-4" />
            <AlertTitle>{t("deleteAccount.ownedTitle", { count: owned.length })}</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>{t("deleteWorkspace.retention")}</p>
              <ul className="space-y-1" data-testid="owned-businesses">
                {owned.map((workspace) => (
                  <li key={workspace.id} className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-foreground">{workspace.name}</span>
                    <Link
                      to="/settings?tab=data"
                      onClick={() => workspace.id !== active?.id && select(workspace.id)}
                      className="text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
                    >
                      {t("deleteAccount.exportLink")}
                    </Link>
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        <div className="space-y-2">
          <Label htmlFor="confirm-account-email">{t("deleteAccount.confirmLabel")}</Label>
          <Input
            id="confirm-account-email"
            type="email"
            value={typed}
            autoComplete="off"
            onChange={(event) => setTyped(event.target.value)}
            data-testid="confirm-account-email"
          />
        </div>
        <Button
          variant="destructive"
          disabled={!matches || busy}
          onClick={() => void remove()}
          data-testid="delete-account-button"
        >
          {t("deleteAccount.button")}
        </Button>
      </CardContent>
    </Card>
  );
}
